import { Link } from "@inertiajs/react";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "@/components/BreadcrumbNavigation";
import { tagPath } from "@/lib/articleTags";

interface Article {
  title: string;
  excerpt: string;
  slug: string;
  published_at: string;
  tags: string[];
  featured_image?: string;
}

interface TagMeta {
  h1: string;
  subtitle: string;
}

type Props = {
  articles: Article[];
  all_tags: string[];
} & (
  | { active_tag: null; tag_meta: null }
  | { active_tag: string; tag_meta: TagMeta }
);

// Deterministic date formatter that works consistently on server and client
// Uses UTC methods to avoid timezone differences between server and client
function formatDate(date: string) {
  const d = new Date(date);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

type PillVariant = "tag" | "neutral";

function FilterPill({
  label,
  active,
  variant,
  href,
}: {
  label: string;
  active: boolean;
  variant: PillVariant;
  href: string;
}) {
  const styles =
    variant === "tag"
      ? active
        ? "bg-brand-accent text-white"
        : "bg-brand-accent/15 text-brand-accent-text dark:text-brand-accent-text-dark hover:bg-brand-accent/30"
      : active
        ? "bg-slate-800 text-white dark:bg-white dark:text-slate-900"
        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700";

  return (
    <Link
      href={href}
      className={`inline-block rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${styles}`}
    >
      {label}
    </Link>
  );
}

const DEFAULT_TITLE = "Infralyst articles, guides & playbooks";
const DEFAULT_SUBTITLE =
  "Guides on AWS cost optimization, rightsizing, and Terraform cost optimization from the Infralyst team.";

export default function Index({ articles, active_tag, all_tags, tag_meta }: Props) {
  const heroSubtitle = tag_meta?.subtitle ?? DEFAULT_SUBTITLE;
  const layoutTitle = tag_meta?.h1 ?? DEFAULT_TITLE;

  return (
    <Layout title={layoutTitle} description={heroSubtitle}>
      {/* Spacer to push content below sticky navbar */}
      <div className="h-20" />
      <div className="pb-16">
        {active_tag && (
          <div className="max-w-3xl mx-auto px-4 pt-8">
            <BreadcrumbNavigation
              items={[
                { label: "Articles", href: "/articles" },
                { label: active_tag },
              ]}
            />
          </div>
        )}
        <div className="max-w-2xl mx-auto text-center pt-16 pb-8 mb-8 sm:mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
            {tag_meta ? (
              tag_meta.h1
            ) : (
              <>
                <span className="text-primary">Infralyst</span> articles, guides & playbooks
              </>
            )}
          </h1>
          <p className="mb-0 text-lg sm:text-xl text-slate-600 dark:text-slate-300 leading-7">
            {heroSubtitle}
          </p>
        </div>
        {/* Tag filter bar */}
        {all_tags.length > 0 && (
          <div className="max-w-3xl mx-auto flex flex-wrap justify-center gap-2 mb-10">
            <FilterPill
              label="All"
              variant="neutral"
              active={!active_tag}
              href="/articles"
            />
            {all_tags.map((tag) => (
              <FilterPill
                key={tag}
                label={tag}
                variant="tag"
                active={active_tag === tag}
                href={tagPath(tag)}
              />
            ))}
          </div>
        )}
        <div className="max-w-3xl mx-auto flex flex-col gap-10">
          {articles.map((article) => (
            <Link
              key={article.slug}
              href={`/articles/${article.slug}`}
              className="group flex flex-col md:flex-row rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 shadow-sm hover:shadow-lg transition-shadow duration-200 overflow-hidden"
            >
              {/* Featured image */}
              {article.featured_image && (
                <div className="md:w-56 flex-shrink-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800 aspect-[16/9] md:aspect-auto md:h-auto overflow-hidden">
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="object-cover w-full h-full rounded-none md:rounded-l-2xl"
                    loading="lazy"
                  />
                </div>
              )}
              {/* Article info */}
              <div className="flex-1 flex flex-col justify-center px-4 py-6 md:py-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {article.tags &&
                    article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block rounded-full bg-brand-accent text-white px-3 py-1 text-xs font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                </div>
                <h2 className="text-2xl font-bold mb-2 group-hover:underline transition">
                  {article.title}
                </h2>
                <p className="text-slate-600 dark:text-slate-300 text-base mb-4 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <span>{formatDate(article.published_at)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-16">
          Want to suggest a topic or contribute an article from your team? Email{" "}
          <a href="mailto:hello@infralyst.io" className="text-primary hover:underline">
            hello@infralyst.io
          </a>.
        </p>
      </div>
    </Layout>
  );
}
